// src/api/expenses.js
import axiosInstance from "./axiosInstance";
import { supabase } from "../supabaseClient";

/** Get a bearer token, if any (Supabase first, then legacy localStorage). */
async function getAccessToken() {
  try {
    const { data: s1, error: e1 } = await supabase.auth.getSession();
    if (!e1 && s1?.session?.access_token) return s1.session.access_token;

    const { data: s2, error: e2 } = await supabase.auth.refreshSession();
    if (!e2 && s2?.session?.access_token) return s2.session.access_token;
  } catch (_) {
    // ignore; fallback below
  }

  const legacy = localStorage.getItem("accessToken");
  return legacy || null;
}

/**
 * POST /api/expenses
 * 1) Try cookie-based auth (withCredentials)
 * 2) If 401/403, retry with Bearer token
 */
export async function createExpense(payload) {
  try {
    // 1) Cookie-first attempt (hits https://api.beemtech.co.uk via axiosInstance)
    const res = await axiosInstance.post("/api/expenses", payload || {}, {
      withCredentials: true,
      headers: { "Content-Type": "application/json" },
    });

    return { data: res?.data };
  } catch (err) {
    const status = err?.response?.status;

    // 2) If unauthorized, retry with Authorization header
    if (status === 401 || status === 403) {
      const token = await getAccessToken();
      if (!token) throw new Error("No active Supabase session");

      const res2 = await axiosInstance.post("/api/expenses", payload || {}, {
        withCredentials: true,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      return { data: res2?.data };
    }

    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to create expense";

    throw new Error(msg);
  }
}

/**
 * GET /api/expenses
 * 1) Try cookie-based auth (withCredentials)
 * 2) If 401/403, retry with Bearer token
 */
export async function listExpenses() {
  try {
    const res = await axiosInstance.get("/api/expenses", {
      withCredentials: true,
    });

    return res?.data;
  } catch (err) {
    const status = err?.response?.status;

    if (status === 401 || status === 403) {
      const token = await getAccessToken();
      if (!token) throw new Error("No active Supabase session");

      const res2 = await axiosInstance.get("/api/expenses", {
        withCredentials: true,
        headers: { Authorization: `Bearer ${token}` },
      });

      return res2?.data;
    }

    const msg =
      err?.response?.data?.error ||
      err?.response?.data?.message ||
      err?.message ||
      "Failed to load expenses";

    throw new Error(msg);
  }
}
